'use client';

import { c as _c } from "react/compiler-runtime";
import { type MouseEvent, PropsWithChildren, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import { MORPH_TRANSITION } from './constants';
type MorphDialogContentProps = {
  morphId: string;
  isOpen: boolean;
  onClose: () => void;
  ariaLabel?: string;
  className?: string;
  maxWidth?: string;
};
export function MorphDialogContent(t0) {
  const $ = _c(41);
  const {
    morphId,
    isOpen,
    onClose,
    ariaLabel,
    className,
    maxWidth: t1,
    children
  } = t0;
  const maxWidth = t1 === undefined ? "800px" : t1;
  const prefersReducedMotion = usePrefersReducedMotion();
  let t2;
  if ($[0] !== onClose) {
    t2 = event => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    $[0] = onClose;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleEscape = t2;
  let t3;
  let t4;
  if ($[2] !== handleEscape || $[3] !== isOpen) {
    t3 = () => {
      if (!isOpen) {
        return;
      }
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    };
    t4 = [isOpen, handleEscape];
    $[2] = handleEscape;
    $[3] = isOpen;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== isOpen) {
    t5 = () => {
      if (!isOpen) {
        return;
      }
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    };
    t6 = [isOpen];
    $[6] = isOpen;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== onClose) {
    t7 = function handleBackdropClick(event_0) {
      if (event_0.target === event_0.currentTarget) {
        onClose();
      }
    };
    $[9] = onClose;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleBackdropClick = t7;
  if (prefersReducedMotion) {
    if (!isOpen) {
      return null;
    }
    let t8;
    if ($[11] !== handleBackdropClick) {
      t8 = <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={handleBackdropClick} />;
      $[11] = handleBackdropClick;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    const t9 = `fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[calc(100%-48px)] overflow-y-auto rounded-3xl bg-white p-6 ${className ?? ""}`;
    let t10;
    if ($[13] !== maxWidth) {
      t10 = {
        maxWidth,
        maxHeight: "90%",
        minHeight: 348
      };
      $[13] = maxWidth;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    let t11;
    if ($[15] !== children) {
      t11 = <div className="flex flex-col gap-6">{children}</div>;
      $[15] = children;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    let t12;
    if ($[17] !== ariaLabel || $[18] !== t10 || $[19] !== t11 || $[20] !== t9) {
      t12 = <FocusScope trapped={true} loop={true}><div role="dialog" aria-modal="true" aria-label={ariaLabel} className={t9} style={t10}>{t11}</div></FocusScope>;
      $[17] = ariaLabel;
      $[18] = t10;
      $[19] = t11;
      $[20] = t9;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    let t13;
    if ($[22] !== t12 || $[23] !== t8) {
      t13 = <div className="fixed inset-0 z-50">{t8}{t12}</div>;
      $[22] = t12;
      $[23] = t8;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    return t13;
  }
  let t8;
  if ($[25] !== handleBackdropClick || $[26] !== isOpen || $[27] !== morphId) {
    t8 = isOpen && <motion.div key={`morph-backdrop-${morphId}`} className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={MORPH_TRANSITION.backdrop} onClick={handleBackdropClick} />;
    $[25] = handleBackdropClick;
    $[26] = isOpen;
    $[27] = morphId;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t8) {
    t9 = <AnimatePresence>{t8}</AnimatePresence>;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== ariaLabel || $[32] !== children || $[33] !== className || $[34] !== isOpen || $[35] !== maxWidth || $[36] !== morphId) {
    t10 = isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"><motion.div layoutId={`morph-container-${morphId}`} role="dialog" aria-modal="true" aria-label={ariaLabel} className={`pointer-events-auto w-[calc(100%-48px)] overflow-y-auto bg-white p-6 ${className ?? ""}`} style={{
        maxWidth,
        maxHeight: "90%",
        minHeight: 348,
        borderRadius: 24,
        originY: 1
      }} transition={MORPH_TRANSITION.layout}><FocusScope trapped={true} loop={true}><div className="flex flex-col gap-6">{children}</div></FocusScope></motion.div></div>;
    $[31] = ariaLabel;
    $[32] = children;
    $[33] = className;
    $[34] = isOpen;
    $[35] = maxWidth;
    $[36] = morphId;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== t10 || $[39] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[38] = t10;
    $[39] = t9;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  return t11;
}